'use client';

import type { PropertySchema } from '@energy-retailer/schema';
import { EnergyType } from '@energy-retailer/constants';
import { forwardRef, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useDebouncedCallback } from 'use-debounce';

// import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { useAddressLookup } from '@/app/lib/hooks/use-address-lookup';
import { NmiSearchModal } from '@/components/nmi-search-modal';
import { Text } from '@/components/text';
import { MultipleAddressModal } from '@/components/multiple-address-modal';
import { Typeahead } from '@/app/components/typeahead/typeahead';
import {
  /*ANALYTICS_EVENTS,*/ NMI_SEARCH_ITEM,
  CONTACT_SEARCH_ITEM,
  MODAL_NAMES,
} from '@/constants';

import { FlowSwitchModal } from '../flow-switch-modal';

import { useIsPlanSearching } from './context';
import { DistributorFilterModal } from './distributor-filter-modal';
import { PreferenceField } from './preference-field';
import { PriceFrequencyField } from './price-frequency-field';

import type { FilteringSchemaType } from './form-schema';
import type { DataType } from '../typeahead/typeahead.types';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import { Checkbox } from '@/components/input';
import { CheckboxSelectionMessaging } from '@/components/checkbox-selection-messaging/checkbox-selection-messaging';
import JoinFormModalRenderer from '../join-form-modal-renderer/join-form-modal-renderer';
import { useConditionallyShowCrossSell } from '@/app/lib/hooks/use-condtionally-show-cross-sell';
import { useAddressDetails } from '@/lib/hooks/use-address-details';

type ProductPlanListingControlsProps = {
  addAddressActive: boolean;
  setAddAddressActive: (addAddressActive: boolean) => void;
};

const ProductPlanListingControls = forwardRef<
  HTMLInputElement,
  ProductPlanListingControlsProps
>((props, ref) => {
  const {
    property,
    resetProperty,
    getCurrentStep,
    currentStepNumber,
    electricityChecked,
    electricityAvailable,
    electricityPlanSlug,
    gasChecked,
    gasAvailable,
    gasPlanSlug,
    selectAddress,
    setCheckboxChecked,
    openModal,
    closeModal,
    getCurrentModal,
  } = useJoinFormStore();

  const currentModal = getCurrentModal();
  const { conditionallyShowCrossSell } = useConditionallyShowCrossSell();
  const { data, isFetching } = useAddressDetails<PropertySchema>(
    property.address.id
  );
  const [isFlowSwitchModalOpen, setIsFlowSwitchModalOpen] =
    useState<boolean>(false);
  const { setIsPlanSearching } = useIsPlanSearching();
  const [searchQuery, setSearchQuery] = useState<string>(
    property.addressSearch ?? ''
  );

  const addressLookup = useAddressLookup<DataType>(searchQuery);

  const addressLookupList = Array.isArray(addressLookup.data)
    ? addressLookup.data
    : [];

  const { setValue } = useFormContext<FilteringSchemaType>();

  const step = getCurrentStep();

  const isAddressSet = Boolean(property.address?.full);

  const debouncedSetSearchQuery = useDebouncedCallback(setSearchQuery, 300);

  useEffect(() => {
    setValue('address', {
      id: property.address.id,
      name: property.addressSearch,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only needs to run on first mount
  }, []);

  useEffect(() => {
    if (step?.fuel === EnergyType.Electricity) {
      setValue(
        'distributor',
        property.electricityDistributors[0]
          ? property.electricityDistributors[0].id
          : undefined
      );
    } else if (step?.fuel === EnergyType.Gas) {
      setValue(
        'distributor',
        property.gasDistributors[0] ? property.gasDistributors[0].id : undefined
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only needs to update on joinFormStore changes
  }, [
    property.address.full,
    currentStepNumber,
    gasChecked,
    gasAvailable,
    electricityChecked,
    electricityAvailable,
  ]);

  const onOptionSelect = async (value: DataType): Promise<void> => {
    setIsPlanSearching(true);

    if (value.id === CONTACT_SEARCH_ITEM.id) {
      openModal(MODAL_NAMES.GasAddressNotFound);
    } else if (value.id === NMI_SEARCH_ITEM.id) {
      openModal(MODAL_NAMES.NmiSearch);
    } else {
      await selectAddress(value);
    }
    setIsPlanSearching(false);
  };

  return (
    <div className="grid grid-cols-12 gap-a pt-b">
      <div
        className={twMerge(
          'col-span-10 col-start-2 flex flex-col gap-b rounded-rad-a bg-white p-c lg:col-span-4',
          props.addAddressActive ? 'border-2 border-pink-600' : ''
        )}
      >
        <Text
          variant="h4"
          as="h4"
        >
          Your Address
        </Text>
        <Typeahead<FilteringSchemaType>
          id="address-lookup-input"
          variant={isAddressSet ? 'disabled' : 'secondary'}
          inputRef={ref}
          name="address"
          placeholder="Start typing..."
          list={addressLookupList}
          onChange={debouncedSetSearchQuery}
          setAddAddressActive={props.setAddAddressActive}
          onOptionSelect={onOptionSelect}
        />
        <Text
          variant="body2"
          className="text-gray-700"
        >
          Enter your address for an accurate quote based on your meter details.
        </Text>
        <div className="flex w-full flex-row items-center gap-c pb-5 pt-2">
          <Checkbox
            name="electricity"
            data-testid="electricity-checkbox"
            checked={electricityChecked}
            label="Electricity"
            onChange={() => {
              conditionallyShowCrossSell(EnergyType.Electricity);
              setCheckboxChecked(EnergyType.Electricity, !electricityChecked);
            }}
            disabled={!electricityAvailable || Boolean(electricityPlanSlug)}
          />
          <Checkbox
            name="gas"
            data-testid="gas-checkbox"
            checked={gasChecked && gasAvailable}
            label="Gas"
            onChange={() => {
              conditionallyShowCrossSell(EnergyType.Gas);
              setCheckboxChecked(EnergyType.Gas, !gasChecked);
            }}
            disabled={!gasAvailable || Boolean(gasPlanSlug)}
          />
        </div>

        <CheckboxSelectionMessaging />

        <JoinFormModalRenderer />

        <MultipleAddressModal
          remainOnPage={true}
          electricityDetails={data}
          isLoading={isFetching}
          isModalOpen={currentModal === MODAL_NAMES.MultipleAddress}
          setIsModalOpen={() => closeModal(MODAL_NAMES.MultipleAddress)}
        />

        <NmiSearchModal
          isModalOpen={currentModal === MODAL_NAMES.NmiSearch}
          setIsModalOpen={() => closeModal(MODAL_NAMES.NmiSearch)}
        />

        <FlowSwitchModal
          isModalOpen={isFlowSwitchModalOpen}
          setIsModalOpen={setIsFlowSwitchModalOpen}
          onCancel={() => {
            setValue('address', {
              id: '',
              name: '',
            });
            resetProperty();
          }}
          description="The address you have entered is electricity only and we can see a gas plan in your cart, would you like to remove this from your cart?"
        />

        {(property.electricityDistributors.length > 1 &&
          step?.fuel === EnergyType.Electricity) ||
        (property.gasDistributors.length > 1 &&
          step?.fuel === EnergyType.Gas) ? (
          <DistributorFilterModal />
        ) : null}
      </div>
      <div className="col-span-4 hidden flex-col gap-b rounded-rad-a bg-white p-c lg:flex">
        <PreferenceField
          labelAs="h4"
          labelVariant="h4"
        />
      </div>
      <div className="col-span-4 hidden flex-col gap-b rounded-rad-a bg-white p-c lg:flex">
        <PriceFrequencyField
          labelAs="h4"
          labelVariant="h4"
        />
      </div>
    </div>
  );
});

ProductPlanListingControls.displayName = 'ProductPlanListingControls';

export { ProductPlanListingControls };
